/* eslint-disable max-len */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Input } from '@codex/basics';
import { Icon } from '@codex/icons';


const Container = styled.div`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
`;
Container.displayName = 'Select_Container';

const SelectIcon = styled(Icon)`
  position: absolute;
  right: 7px;
  top: 9px;
  box-sizing: content-box;
  padding: 0px 8px;
  ${({ rotate, theme }) => `
      padding-top: ${rotate === 270 ? '9px' : 0};
      padding-bottom: ${rotate === 90 ? '9px' : 0};
      border-top: ${rotate === 270 ? `1px solid ${theme.colors.shades[4]}` : 'none'};
      border-bottom: ${rotate === 90 ? `1px solid ${theme.colors.shades[4]}` : 'none'};
  `};
  
`;
SelectIcon.displayName = 'Select_SelectIcon';

const StyledInput = styled(Input)`
  text-overflow: ellipsis;
`;

const ItemsContainer = styled.div`
  position: absolute;
  width: 100%;
  z-index: 5;
  border-radius: 4px;
  box-shadow: 1px 1px 3px 0 rgba(102, 102, 102, 0.2);
  background-color: white;
  overflow: auto;
  left: 0;
  right: 0;
  cursor: pointer;
`;
ItemsContainer.displayName = 'Select_ItemsContainer';

export const SelectItem = styled.div`
  padding: 10px 15px;  
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  
  ${({ theme, highlighted, selected }) => `
    border: 1px solid ${theme.colors.shades[5]};
    border-top: none;
    color: ${theme.colors.shades[1]};
    background-color: ${highlighted ? theme.colors.shades[6] : 'white'};
    font-weight: ${selected ? 'bold' : 'normal'};
  `}
`;
SelectItem.displayName = 'Select_SelectItem';

const Select = (props) => {
  const [open, setOpen] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(-1);

  const selectElement = useRef(null);
  const inputElement = useRef(null);

  const handleClickOutsideCallback = useCallback((e) => {
    if (open) {
      if (selectElement.current && !selectElement.current.contains(e.target)) {
        setOpen(false);
      }
    }

    setHighlightedOptionIndex(-1);

  }, [open, selectElement]);

  useEffect(() => {
    if (open) {
      document.addEventListener('click', handleClickOutsideCallback);

      return () => {
        document.removeEventListener('click', handleClickOutsideCallback);
      };
    }
    return () => {};
  });


  const keydownHandlerCallback = useCallback((e) => {
    if (!open) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (highlightedOptionIndex === props.options.length - 1) {
          setHighlightedOptionIndex(0);
        } else {
          setHighlightedOptionIndex((prevHighlightedOptionIndex) => prevHighlightedOptionIndex + 1);
        }

        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (highlightedOptionIndex === -1 || highlightedOptionIndex === 0) {
          setHighlightedOptionIndex(props.options.length - 1);
        } else {
          setHighlightedOptionIndex((prevHighlightedOptionIndex) => prevHighlightedOptionIndex - 1);
        }
        break;
      }

      case 'Enter': {
        e.preventDefault();
        if (highlightedOptionIndex !== -1) {
          setSelectedOptionIndex(highlightedOptionIndex);
          setOpen(false);
        }
        break;
      }

      default:
        break;
    }
  }, [open, highlightedOptionIndex]);


  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', keydownHandlerCallback);
      return () => {
        document.removeEventListener('keydown', keydownHandlerCallback);
      };
    }
    return () => {};
  });

  useEffect(() => {
    if (selectedOptionIndex !== -1) {
      props.onSelectedItemChange(props.options[selectedOptionIndex], selectedOptionIndex);
    }
  }, [selectedOptionIndex]);

  useEffect(() => {
    if (props.clearSelectionWhenDisabled && props.disabled) {
      setSelectedOptionIndex(-1);
    }
  }, [props.disabled, props.clearSelectionWhenDisabled]);


  return (
    <Container
      ref={selectElement}
      onClick={(e) => {
        if (inputElement.current.contains(e.target)) {
          if (!props.disabled) {
            setOpen((prevOpen) => !prevOpen);
          }
        }
      }}
      data-qa={props.qa}
      data-interaction={props.interaction}
      className={props.className}
    >
      <StyledInput
        inputElementRef={inputElement}
        interactivePlaceholder
        disabled={props.disabled}
        placeholderText={props.label}
        readOnly
        value={
          (selectedOptionIndex === -1 &&
            props.defaultOptionIndex !== -1 && props.options[props.defaultOptionIndex].label) ||
          (selectedOptionIndex !== -1 ? props.options[selectedOptionIndex].label : '')
        }
        attrs={{
          input: {
            style: {
              boxSizing: 'border-box',
              padding: '13px 0px 13px 15px',
              height: '43px',
              width: '100%',
              border: '1px solid #999',
              borderRadius: '4px',
              fontSize: '13px'
            }
          }
        }}
      />
      <SelectIcon
        icon={'ChevronRight'}
        rotate={open ? 270 : 90}
        onClick={() => {
          if (!props.disabled) {
            setOpen((prevOpen) => !prevOpen);
          }
        }}
      />
      {open && (
        <ItemsContainer>
          {props.options
            .map((option, index) => (
              <SelectItem
                onClick={() => {
                  setSelectedOptionIndex(index);
                  setOpen(false);
                }}
                onMouseEnter={() => { setHighlightedOptionIndex(index); }}
                key={option.id}
                highlighted={
                  (highlightedOptionIndex === index) ||
                  (selectedOptionIndex === index && highlightedOptionIndex === -1) ||
                  (props.defaultOptionIndex === index && highlightedOptionIndex === -1 && selectedOptionIndex === -1)
                }
                selected={((selectedOptionIndex === index) ||
                  (props.defaultOptionIndex === index && selectedOptionIndex === -1))}
              >
                {option.label}
              </SelectItem>
            ))}
        </ItemsContainer>
      )}
    </Container>
  );
};

Select.propTypes = {
  defaultOptionIndex: PropTypes.number,
  disabled: PropTypes.bool,
  clearSelectionWhenDisabled: PropTypes.bool,
  interaction: PropTypes.string,
  label: PropTypes.string,
  onSelectedItemChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string
  })),
  qa: PropTypes.string,
  valid: PropTypes.bool,
  className: PropTypes.string
};

Select.defaultProps = {
  defaultOptionIndex: -1,
  disabled: false,
  clearSelectionWhenDisabled: false,
  interaction: null,
  label: null,
  onSelectedItemChange: null,
  options: [],
  qa: undefined,
  valid: true,
  className: undefined
};

export default Select;
